/**
 * Admin CRUD schemas for campaigns (Story 3.8).
 *
 * Source of truth: docs/architecture/database-schema.md §4.4 (campaigns +
 * campaign_type) and Story 3.8 §AC #2 (config JSONB shape).
 *
 * **Type semantics:** `campaign.type` is immutable after create. The DB enforces
 * `UNIQUE (type) WHERE active = TRUE` (partial), so only one campaign per type
 * can be active at any time. Toggling activation goes through
 * `POST /admin/campaigns/:id/activate` which atomically deactivates the
 * incumbent (if any) before flipping the new row.
 *
 * **Config shape:**
 *   ```json
 *   {
 *     "cadence":  { "c1": "immediate", "c2_time": "09:00", "c3_offset": "-1h" },
 *     "templates": { "c1": "...", "c2": "...", "c3": "..." },
 *     "business_hours_override": null,
 *     "prompt_id": "<uuid>"
 *   }
 *   ```
 *
 * `business_hours_override` is `null` when the campaign defers to the global
 * `system_settings.business_hours` (Story 1.6 default). When non-null, the
 * scheduler (Story 3.2) uses the override verbatim.
 *
 * Cross-field validation (cadence ordering, template-must-be-APPROVED,
 * prompt-must-exist) is enforced server-side, NOT in TypeBox — see the
 * Campaigns route for the runtime checks.
 */
import { Type } from '@sinclair/typebox';
/**
 * Campaign types — mirrors `campaign_type` enum in `db/schema.ts`.
 */
export const CampaignType = Type.Union([Type.Literal('confirmation'), Type.Literal('lead_new'), Type.Literal('reactivation')], { $id: 'CampaignType' });
export const CAMPAIGN_TYPES = [
    'confirmation',
    'lead_new',
    'reactivation',
];
/**
 * Cadence values follow Story 3.8 §AC #2:
 *   - `c1`: `'immediate'` (only literal currently allowed)
 *   - `c2_time`: HH:MM (24h, e.g. `'09:00'`)
 *   - `c3_offset`: signed duration string (e.g. `'-1h'`, `'-30m'`, `'-2h'`)
 *
 * The pattern below is permissive enough to allow PRD §FR3 future overrides
 * without breaking the type contract. Cross-field ordering (C2 chronologically
 * before C3 relative to meeting) is checked at the route layer.
 */
export const CADENCE_TIME_PATTERN = '^([01]\\d|2[0-3]):[0-5]\\d$';
export const CADENCE_OFFSET_PATTERN = '^-?\\d+(h|m)$';
/**
 * C4 ("lembrete na hora") offset — same signed-duration grammar as
 * `c3_offset` (minutes `m` or hours `h`), plus a bare `0` so the operator can
 * express "no exact moment of the meeting" without a unit. Empty string ⇒ the
 * touch is DISABLED (no C4 scheduled). Examples: `-1m`, `-10m`, `-1h`, `0`.
 */
export const CADENCE_C4_OFFSET_PATTERN = '^(0|-?\\d+(h|m))$';
export const CampaignCadence = Type.Object({
    c1: Type.Literal('immediate'),
    c2_time: Type.String({ pattern: CADENCE_TIME_PATTERN }),
    c3_offset: Type.String({ pattern: CADENCE_OFFSET_PATTERN }),
    // C4 ("lembrete na hora") — OPTIONAL. Empty/absent ⇒ touch disabled.
    // Accepts minutes (`-1m`, `-10m`), hours (`-1h`) or a bare `0`.
    c4_offset: Type.Optional(Type.Union([Type.String({ pattern: CADENCE_C4_OFFSET_PATTERN }), Type.Literal('')])),
}, { $id: 'CampaignCadence' });
/**
 * 2026-07-21 (user request) — A/B/C test variant for the opening template.
 * `key` is a short, operator-facing label ("A"/"B"/"C" by convention, but any
 * non-empty string works) used for reporting; `templateName` must reference
 * an approved row in the templates registry, same as the base `templateName`.
 * Mirrors `OpeningTemplateVariant` in `followup-config.ts` — keep both in sync.
 *
 * Declared here (before `CampaignTemplates` and `CampaignFollowupInitial`,
 * both of which reference it) because TypeBox schemas are plain `const`s
 * evaluated top-to-bottom at module load — a forward reference throws
 * "Cannot access before initialization" (TDZ), unlike a type-level reference
 * which the compiler resolves regardless of order.
 */
export const OpeningTemplateVariant = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 32 }),
    templateName: Type.String({ minLength: 1 }),
}, { $id: 'OpeningTemplateVariant' });
/**
 * 2026-07-30 (user request) — structurally identical to `OpeningTemplateVariant`
 * above, but with its OWN `$id`. A single `Campaign` response embeds BOTH
 * `templates.c1_variants` (this type) and `followup.initial.variants`
 * (`OpeningTemplateVariant`) in the same schema tree; ajv requires every
 * `$id` to be unique across that whole tree, and reusing `OpeningTemplateVariant`
 * for both nesting points made Fastify's serializer fail at boot with
 * `reference "OpeningTemplateVariant" resolves to more than one schema` —
 * ajv doesn't dedupe by object identity when the SAME $id'd fragment is
 * embedded inline (not via `$ref`) at two different locations.
 */
export const CampaignC1Variant = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 32 }),
    templateName: Type.String({ minLength: 1 }),
}, { $id: 'CampaignC1Variant' });
/**
 * Templates referenced by a campaign — keys mirror cadence keys.
 * Each value is a template **name** (unique with language `pt_BR`); the route
 * resolves it to a row and checks `status === 'APPROVED'` before persisting.
 */
export const CampaignTemplates = Type.Object({
    // c1/c2/c3 are required (non-empty) for campaigns still on the legacy
    // cadence engine — but a campaign that has adopted `followup` (Story
    // 3.20) doesn't use these at all, so the TYPE allows empty here; the
    // route (`validateConfigPayload`/`validateTemplatesApproved`) is where
    // "required unless config.followup is set" is enforced, per this file's
    // own cross-field-validation-lives-in-the-route convention.
    c1: Type.String(),
    c2: Type.String(),
    c3: Type.String(),
    // C4 ("lembrete na hora") template — OPTIONAL. Empty/absent ⇒ touch
    // disabled. When set, the route validates it exists + APPROVED + active.
    c4: Type.Optional(Type.String()),
    // 2026-07-30 (user request) — A/B/C test for C1 (confirmação inicial),
    // same shape/spirit as `followup.initial.variants` for Lead Novo's
    // opening template. Absent/empty means "no split, always use `c1`" (the
    // pre-existing behavior). Deliberately scoped to C1 only — C2/C3/C4 keep
    // a single fixed template each; reuses `CampaignC1Variant` above (a
    // separate $id from `OpeningTemplateVariant` — see that type's docstring
    // for why the two can't share an $id here).
    c1_variants: Type.Optional(Type.Array(CampaignC1Variant, { maxItems: 10 })),
}, { $id: 'CampaignTemplates' });
/**
 * Story 3.20 — followup por inatividade (motor genérico, adotado primeiro por
 * `lead_new`; confirmation/reactivation migram nas Stories 3.21/3.22).
 *
 * Substitui o modelo antigo de `cadence` ancorado no horário da reunião por
 * um modelo ancorado em SILÊNCIO na conversa:
 *   - `initial`: tempo até o primeiro contato após o gatilho da campanha
 *     (`immediate` ou `delay_minutes`).
 *   - `steps`: até 5 cutucadas, cada uma disparando `afterMinutes` depois da
 *     última atividade (mensagem da IA OU resposta do cliente, o que for mais
 *     recente). Total de toques (`initial` + `steps`) limitado a 6.
 *
 * O worker de followup (`followup-scheduler.worker.ts`) roda por polling —
 * qualquer campanha com `followup` presente no config fica automaticamente
 * elegível, independente de `type`.
 */
export const CampaignFollowupInitial = Type.Object({
    trigger: Type.Union([Type.Literal('immediate'), Type.Literal('delay_minutes')]),
    delayMinutes: Type.Optional(Type.Integer({ minimum: 1, maximum: 10080 })),
    templateName: Type.String({ minLength: 1 }),
    // Optional A/B/C split — absent/empty means "no split, always use
    // `templateName`" (pre-existing behavior). See `followup-config.ts`'s
    // `pickOpeningTemplateVariant` for the deterministic selection logic.
    variants: Type.Optional(Type.Array(OpeningTemplateVariant, { maxItems: 10 })),
}, { $id: 'CampaignFollowupInitial' });
export const CampaignFollowupStep = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 64 }),
    afterMinutes: Type.Integer({ minimum: 1, maximum: 10080 }),
    templateName: Type.String({ minLength: 1 }),
}, { $id: 'CampaignFollowupStep' });
/** Máximo de toques TOTAIS (initial + steps) por campanha. */
export const FOLLOWUP_MAX_TOTAL_TOUCHES = 6;
/** Máximo de followup steps (o 6º toque possível é sempre o `initial`). */
export const FOLLOWUP_MAX_STEPS = FOLLOWUP_MAX_TOTAL_TOUCHES - 1;
export const CampaignFollowup = Type.Object({
    initial: CampaignFollowupInitial,
    steps: Type.Array(CampaignFollowupStep, { maxItems: FOLLOWUP_MAX_STEPS }),
}, { $id: 'CampaignFollowup' });
/**
 * Business-hours override (per-campaign, optional). Mirrors the global
 * `system_settings.business_hours` shape: timezone + start/end + working
 * weekdays (0=Sun..6=Sat). `null` falls back to the global default.
 */
export const BusinessHours = Type.Object({
    timezone: Type.String({ minLength: 1 }),
    start: Type.String({ pattern: CADENCE_TIME_PATTERN }),
    end: Type.String({ pattern: CADENCE_TIME_PATTERN }),
    weekdays: Type.Array(Type.Integer({ minimum: 0, maximum: 6 }), { minItems: 1, maxItems: 7 }),
}, { $id: 'BusinessHours' });
/**
 * Story 5.2 — opening template variables for the lead-new HSM
 * `abertura_lead_novo`. Free-form `{ key: string }` map consumed by the
 * worker to build the WhatsApp `components.parameters` payload (e.g.
 * `{ nome: "{{customer.name}}" }`). Keys are template placeholder names;
 * values are interpolation expressions or static strings. The worker
 * resolves customer-scoped placeholders at send time; unknown keys pass
 * through verbatim so future templates can extend the map without code
 * changes. Story 5.8 exposes this on the campaign detail UI.
 */
export const OpeningTemplateVars = Type.Record(Type.String({ minLength: 1 }), Type.String(), {
    $id: 'OpeningTemplateVars',
});
/**
 * Full campaign config — Story 3.8 §AC #2.
 *
 * `prompt_id` is the FK pointer to `prompts.id`; the route enforces existence
 * before write. It MAY be empty-string at create time when no prompt exists yet
 * (Story 3.7 has not run for this campaign), in which case the campaign cannot
 * be activated until a prompt is bound.
 *
 * Story 5.2 — two optional fields for the lead-new flow:
 *   - `opening_template_name`: HSM template to send on conversation creation
 *     (defaults to `abertura_lead_novo` when absent — see lead-new.worker.ts).
 *   - `opening_template_vars`: map of placeholder→value used to build the
 *     template components payload.
 * Both are optional so confirmation/reactivation campaigns don't have to
 * specify them, and so existing rows in `campaigns.config` keep validating
 * against the schema during the 5.2 release window.
 */
export const CampaignConfig = Type.Object({
    cadence: CampaignCadence,
    templates: CampaignTemplates,
    business_hours_override: Type.Union([BusinessHours, Type.Null()]),
    prompt_id: Type.Union([Type.String({ format: 'uuid' }), Type.Literal('')]),
    // Story 5.2 — lead-new opening template wiring (Story 5.8 exposes in UI).
    opening_template_name: Type.Optional(Type.String({ minLength: 1 })),
    opening_template_vars: Type.Optional(OpeningTemplateVars),
    // Story 5.6 — reactivation message pool (free-form). Worker rotates the
    // entries per conversation via `last_reactivation_msg_idx`. Empty/missing
    // → cron skips with `reactivation.skipped_no_pool`. Production guidance
    // (BACKLOG-MED-E5-1): convert pool to HSM templates once approved by
    // Meta; the field `reactivation_template_pool` will hold template names
    // and take precedence over `reactivation_pool` when present (future story).
    reactivation_pool: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 1000 }), { maxItems: 50 })),
    // Story 5.7 — manager phone (E.164) that receives the cold-lead alert
    // when a conversation transitions to `status='cold'`. When omitted, the
    // cold-handler falls back to `system_settings.handoff_operators[0]`.
    manager_phone: Type.Optional(Type.String({ minLength: 8, maxLength: 16 })),
    // Story 5.8 — lead-new config fields (admin-editable in CampaignDetail).
    // `disqualification_criteria` is interpolated into the qualification
    // prompt as `{disqualification_criteria}` — Story 5.3 documents the
    // variable. Free-form text; manager curates wording.
    disqualification_criteria: Type.Optional(Type.String({ maxLength: 2000 })),
    // `seller_pool` restricts SellerDistributor (Story 5.4) to this subset of
    // active sellers. Empty/missing → all active sellers eligible.
    seller_pool: Type.Optional(Type.Array(Type.String({ format: 'uuid' }), { maxItems: 50 })),
    // Story 5.8 — reactivation config fields. `reactivation_thresholds` is
    // a per-campaign override of the global `system_settings.reactivation_thresholds`.
    // When present, the worker prefers this list over the global one.
    reactivation_thresholds: Type.Optional(Type.Array(Type.String({ minLength: 2, maxLength: 16 }), { minItems: 1, maxItems: 10 })),
    // `reactivation_notify_manager` toggles the BACKLOG-LOW-5.7-L2 alert
    // sent when a conversation goes cold. Default true (notify).
    reactivation_notify_manager: Type.Optional(Type.Boolean()),
    // Story 3.20 — motor de followup por inatividade. Opcional: campanhas que
    // ainda não adotaram o motor novo (confirmation/reactivation até as
    // Stories 3.21/3.22) seguem sem este campo. Presença de `followup` é o
    // único gate que `followup-scheduler.worker.ts` usa para decidir se uma
    // campanha está elegível — não há checagem por `type`.
    followup: Type.Optional(CampaignFollowup),
}, { $id: 'CampaignConfig' });
/**
 * `Campaign` — full read-side projection returned by GET endpoints.
 */
export const Campaign = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String({ minLength: 1 }),
    type: CampaignType,
    active: Type.Boolean(),
    config: CampaignConfig,
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
/**
 * `CampaignCreate` — payload for `POST /admin/campaigns` (admin/manager).
 * Server inserts with `active = false` regardless of any client hint.
 */
export const CampaignCreate = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 200 }),
    type: CampaignType,
    config: CampaignConfig,
});
/**
 * `CampaignUpdate` — payload for `PATCH /admin/campaigns/:id` (admin/manager).
 *
 * `type` is intentionally absent: changing the discriminator after creation
 * would invalidate cadence/templates and bypass the partial-unique constraint.
 */
export const CampaignUpdate = Type.Object({
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    config: Type.Optional(CampaignConfig),
});
/**
 * `CampaignListQuery` — `GET /admin/campaigns?type=&active=`.
 */
export const CampaignListQuery = Type.Object({
    type: Type.Optional(CampaignType),
    active: Type.Optional(Type.String()),
});
export const CampaignListResponse = Type.Object({
    campaigns: Type.Array(Campaign),
});
export const CampaignResponse = Type.Object({
    campaign: Campaign,
});
/**
 * Pure helper — true when cadence values look reasonable. Intended for both
 * UI live-validation and server-side gating.
 *
 * Returns null on success or a human-readable reason in pt_BR.
 */
export function validateCadence(c) {
    // c1 is fixed to 'immediate' — TypeBox already enforces.
    // c2_time + c3_offset already match patterns; nothing further to check
    // without resolving relative to the meeting time, which the scheduler does.
    // We still re-check the patterns so callers that built the object manually
    // don't bypass the contract.
    if (!new RegExp(CADENCE_TIME_PATTERN).test(c.c2_time)) {
        return 'cadence.c2_time deve estar no formato HH:MM.';
    }
    if (!new RegExp(CADENCE_OFFSET_PATTERN).test(c.c3_offset)) {
        return 'cadence.c3_offset deve estar no formato signed duration (ex: -1h, -30m).';
    }
    // c4_offset é OPCIONAL — só validamos quando preenchido. Vazio/ausente =
    // toque "lembrete na hora" desligado. Aceita minutos (-1m), horas (-1h) ou 0.
    if (c.c4_offset !== undefined &&
        c.c4_offset !== '' &&
        !new RegExp(CADENCE_C4_OFFSET_PATTERN).test(c.c4_offset)) {
        return 'cadence.c4_offset deve estar no formato signed duration (ex: -1m, -10m, -1h, 0) ou vazio.';
    }
    return null;
}
/**
 * Default cadence shape used by the admin form when starting a new campaign
 * or when a legacy seed row is missing keys.
 */
export const DEFAULT_CAMPAIGN_CADENCE = {
    c1: 'immediate',
    c2_time: '09:00',
    c3_offset: '-1h',
};
/**
 * Pure helper — valida `templates.c1_variants` (2026-07-30, A/B/C test do C1
 * de confirmação): cada `key` (após trim) não pode ser vazia nem duplicada.
 * Mesma checagem de `validateFollowupConfig` para `followup.initial.variants`
 * — sem isso no write-path, uma duplicata só falharia depois, silenciosamente,
 * no worker (`resolveC1Template`/`pickOpeningTemplateVariant`).
 *
 * Retorna `null` em sucesso ou uma mensagem pt_BR legível.
 */
export function validateCampaignTemplates(t) {
    const seenVariantKeys = new Set();
    for (const variant of t.c1_variants ?? []) {
        const key = variant.key.trim();
        if (key.length === 0) {
            return 'templates.c1_variants tem uma variante com "key" vazia (ou só espaços).';
        }
        if (seenVariantKeys.has(key)) {
            return `templates.c1_variants tem uma "key" duplicada: "${key}".`;
        }
        seenVariantKeys.add(key);
    }
    return null;
}
/**
 * Pure helper — valida um `CampaignFollowup` (Story 3.20 §AC #2):
 *   - `initial.delayMinutes` obrigatório quando `trigger='delay_minutes'`.
 *   - Teto de `FOLLOWUP_MAX_TOTAL_TOUCHES` toques totais (`1 + steps.length`).
 *   - `afterMinutes` estritamente crescente entre steps consecutivos (evita
 *     um step 2 disparar antes do step 1).
 *   - `id` de cada step único.
 *   - 2026-07-21 (QA finding — Story 8.2): `key` de cada variante A/B/C não
 *     vazio (após trim) e único. Sem essa checagem no write-path, uma
 *     duplicata salva pela UI só falha depois, silenciosamente, quando
 *     `extractFollowupConfig` (apps/api) rejeita o `initial.variants`
 *     inteiro e derruba a campanha pro fallback legado — inclusive
 *     desligando os `steps[]` configurados junto.
 *
 * Retorna `null` em sucesso ou uma mensagem pt_BR legível.
 */
export function validateFollowupConfig(f) {
    if (f.initial.trigger === 'delay_minutes' && f.initial.delayMinutes === undefined) {
        return 'followup.initial.delayMinutes é obrigatório quando trigger="delay_minutes".';
    }
    const totalTouches = 1 + f.steps.length;
    if (totalTouches > FOLLOWUP_MAX_TOTAL_TOUCHES) {
        return `followup permite no máximo ${FOLLOWUP_MAX_TOTAL_TOUCHES} toques no total (initial + steps); recebido ${totalTouches}.`;
    }
    const seenIds = new Set();
    let previousAfterMinutes = 0;
    for (const step of f.steps) {
        if (seenIds.has(step.id)) {
            return `followup.steps tem um "id" duplicado: "${step.id}".`;
        }
        seenIds.add(step.id);
        if (step.afterMinutes <= previousAfterMinutes) {
            return `followup.steps deve ter "afterMinutes" estritamente crescente entre etapas (step "${step.id}" com ${step.afterMinutes}min não é maior que a etapa anterior).`;
        }
        previousAfterMinutes = step.afterMinutes;
    }
    const seenVariantKeys = new Set();
    for (const variant of f.initial.variants ?? []) {
        const key = variant.key.trim();
        if (key.length === 0) {
            return 'followup.initial.variants tem uma variante com "key" vazia (ou só espaços).';
        }
        if (seenVariantKeys.has(key)) {
            return `followup.initial.variants tem uma "key" duplicada: "${key}".`;
        }
        seenVariantKeys.add(key);
    }
    return null;
}
//# sourceMappingURL=campaigns.js.map